import moment, { Moment } from 'moment';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import MDatePickerView from './MDatePicker.view';
import { TimeFormat } from 'utils/enums/time-format';
import { InputType } from './MDatePicker.model';
import useDebounceState from 'utils/hooks/use-debounce-state';

type PropsBase = {
  value?: moment.Moment | null;
  showSelectedMonth?: boolean;
  hideActions?: boolean;
  withTime?: boolean;
  inputType?: InputType;
  position: 'top' | 'bottom';
  className?: string;
  inputClassName?: string;
  maxDate?: Moment;
  minDate?: Moment;
  fromYear?: number;
  toYear?: number;
  dropdown?: boolean;
  disabled?: boolean;
  clearable?: boolean;
  invalid?: boolean;
  error?: string | null;
  closeOnSelection?: boolean;
  debounced?: number;
  onBlur?: (e: React.FocusEvent<HTMLDivElement>) => void;
  onChange: (date: moment.Moment) => void;
};

type ClearableProps = PropsBase & {
  // When clearable is true, date is optional to support 'undefined' value on clear
  clearable: true;
  onChange: (date?: moment.Moment) => void;
};

type Props = PropsBase | ClearableProps;

const MDatePicker: React.FC<Props> = (props: React.PropsWithChildren<Props>) => {
  const dateFormat = useMemo(() => {
    switch (props.inputType) {
      case InputType.Date:
        return 'YYYY-MM-DD';
      case InputType.DatetimeLocal:
        return 'YYYY-MM-DDTHH:mm';
      default:
        return 'YYYY-MM-DD';
    }
  }, [props.inputType]);

  const [datePickerState, debouncedDatePickerState, setDatePickerState] = useDebounceState<Moment | undefined | null>(
    props.value,
    props.debounced!,
  );
  const [isOpenPickerState, setOpenPickerState] = useState<boolean>(false);

  const datePickerRef = useRef<HTMLDivElement>(null);

  const reset = (event: React.MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();
    props.onChange(moment());
  };

  const nextDay = () => {
    if (!props.value) {
      return;
    }

    props.onChange(props.value.clone().add({ days: 1 }));
  };

  const prevDay = () => {
    if (!props.value) {
      return;
    }

    props.onChange(props.value.clone().add({ days: -1 }));
  };

  useEffect(() => {
    if (isOpenPickerState && datePickerRef.current) {
      const handleClickOutside = (event: MouseEvent) => {
        if (
          datePickerRef.current &&
          datePickerRef.current !== event.target &&
          !datePickerRef.current?.contains(event.target as HTMLElement)
        ) {
          setOpenPickerState(() => false);
        }
      };

      document.addEventListener('click', handleClickOutside);

      return () => {
        document.removeEventListener('click', handleClickOutside);
      };
    }
  }, [isOpenPickerState, datePickerRef, setOpenPickerState]);

  useEffect(() => {
    if (debouncedDatePickerState && !props.onBlur) {
      props.onChange(debouncedDatePickerState);
    }
  }, [debouncedDatePickerState]);

  useEffect(() => {
    setDatePickerState(props.value);
  }, [props.value?.format()]);

  const onDatePickerChange = (date?: Date, time?: string) => {
    if (props.clearable && !date && !time) {
      (props as ClearableProps).onChange();

      return;
    }

    const dateTime = moment(date).set({ hours: 23, minutes: 59 });

    if (time) {
      const [hours, minutes] = time.split(':');

      dateTime.set({ hours: Number(hours || 0), minutes: Number(minutes || 0) });
    }

    setDatePickerState(dateTime);

    if (props.closeOnSelection) {
      setOpenPickerState(() => false);
    }
  };

  const handleBlur = useCallback(
    (e: React.FocusEvent<HTMLDivElement>) => {
      const currentTarget = e.currentTarget;
      const blurEvent = e;

      // relatedTarget is null when clicking non-focusable elements
      // Defer the check so the browser has time to settle focus on the new target.
      requestAnimationFrame(() => {
        if (currentTarget.contains(document.activeElement)) {
          return;
        }

        if (datePickerState) {
          props.onChange(datePickerState);
        }

        props.onBlur?.(blurEvent);
      });
    },
    [props.onBlur, props.onChange, datePickerState],
  );

  return (
    <MDatePickerView
      className={props.className}
      minDate={props.minDate}
      maxDate={props.maxDate}
      inputClassName={props.inputClassName}
      withTime={props.withTime}
      time={datePickerState?.format(TimeFormat.Time)}
      isOpenPicker={isOpenPickerState}
      datePickerRef={datePickerRef}
      hideActions={props.hideActions}
      disabled={props.disabled}
      onOpen={() => setOpenPickerState(() => true)}
      onClose={() => setOpenPickerState(() => false)}
      datePicker={datePickerState}
      onDatePickerChange={onDatePickerChange}
      inputType={props.inputType}
      dateFormat={dateFormat}
      clearable={props.clearable}
      reset={reset}
      position={props.position}
      nextDay={nextDay}
      prevDay={prevDay}
      fromYear={props.fromYear}
      toYear={props.toYear}
      invalid={props.invalid}
      error={props.error}
      dropdown={props.dropdown}
      onBlur={props.onBlur ? handleBlur : undefined}
      showSelectedMonth={props.showSelectedMonth}
    ></MDatePickerView>
  );
};

MDatePicker.displayName = 'MDatePicker';
MDatePicker.defaultProps = {
  debounced: 250,
};

export default MDatePicker;
